// Persistent layout: app bar + outlet + bottom tab bar. The router's outlet is
// the inner <main>; the shell itself never re-renders.

import { auth } from '../core/auth.js';
import { api } from '../core/api.js';
import { toggleTheme, currentTheme } from '../core/theme.js';
import { esc } from '../core/component.js';
import { icon } from '../core/icons.js';
import { t, getLocale, setLocale, availableLocales, getLocaleTag } from '../core/i18n.js';
import { NotificationsBell } from './NotificationsBell.js';

export class AppShell {
  constructor(root) {
    this.root = root;
    this.outlet = null;
    this._unsubAuth = null;
  }

  mount() {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = getLocaleTag();
    }
    const locale = getLocale();
    const localeOptions = availableLocales().map((code) =>
      `<option value="${code}" ${code === locale ? 'selected' : ''}>${esc(code.toUpperCase())}</option>`
    ).join('');
    this.root.innerHTML = `
      <div class="app-shell">
        <header class="appbar">
          <a class="appbar-brand" href="#${esc(auth.defaultPath())}" aria-label="${esc(t('app.name'))}">
            ${this._brandLogo()}
            <h1>${esc(t('app.name'))}</h1>
          </a>
          <div class="row appbar-actions">
            <span class="appbar-version" data-role="version" title="${esc(buildLabel())}">${esc(versionLabel())}</span>
            <span class="appbar-status" aria-hidden="true"><span class="dot"></span><b>${esc(t('app.status_label') || 'Live')}</b></span>
            <span data-slot="bell" hidden></span>
            <select class="select select-compact" data-action="locale" aria-label="${esc(t('app.language'))}">${localeOptions}</select>
            <button class="btn btn-ghost btn-icon" data-action="theme" aria-label="${esc(t('app.theme_toggle'))}">${this._themeIcon()}</button>
            <div class="user-menu" data-role="user-menu" hidden>
              <button type="button" class="user-menu-trigger" data-action="user-menu-toggle"
                      aria-haspopup="menu" aria-expanded="false" aria-label="${esc(t('app.logout'))}">
                <span class="avatar-initials" data-slot="avatar"></span>
              </button>
              <div class="user-menu-panel" data-slot="user-menu-panel" role="menu" hidden>
                <div class="user-menu-header">
                  <strong data-slot="menu-name"></strong>
                  <span class="faint" data-slot="menu-email"></span>
                </div>
                <button type="button" class="user-menu-item" data-action="logout" role="menuitem">
                  ${icon('log-out', { size: 'sm' })}<span>${esc(t('app.logout'))}</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        <div class="app-body">
          <aside class="sidebar" data-role="sidebar" hidden>
            <nav class="sidebar-nav" aria-label="${esc(t('app.name'))}">
              <div class="sidebar-group" data-role="side-overview">
                <div class="sidebar-group-label eyebrow">${esc(t('app.nav.group.overview'))}</div>
                <a class="sidebar-link" href="#/dashboard" data-tab="/dashboard" data-role="side-dashboard">
                  <span class="sidebar-num">00</span>
                  <span class="sidebar-icon">${icon('gauge')}</span>
                  <span class="sidebar-label">${esc(t('tabs.dashboard'))}</span>
                </a>
              </div>
              <div class="sidebar-group" data-role="side-setup">
                <div class="sidebar-group-label eyebrow">${esc(t('app.nav.group.setup'))}</div>
                <a class="sidebar-link" href="#/properties" data-tab="/properties" data-role="side-properties">
                  <span class="sidebar-num">01</span>
                  <span class="sidebar-icon">${icon('building')}</span>
                  <span class="sidebar-label">${esc(t('tabs.properties'))}</span>
                </a>
              </div>
              <div class="sidebar-group">
                <div class="sidebar-group-label eyebrow">${esc(t('app.nav.group.daily'))}</div>
                <a class="sidebar-link" href="#/tenancies" data-tab="/tenancies">
                  <span class="sidebar-num">02</span>
                  <span class="sidebar-icon">${icon('file-text')}</span>
                  <span class="sidebar-label">${esc(t('tabs.tenancies'))}</span>
                </a>
                <a class="sidebar-link" href="#/tenants" data-tab="/tenants" data-role="side-tenants">
                  <span class="sidebar-num">03</span>
                  <span class="sidebar-icon">${icon('users')}</span>
                  <span class="sidebar-label">${esc(t('tabs.tenants'))}</span>
                </a>
                <a class="sidebar-link" href="#/tasks" data-tab="/tasks">
                  <span class="sidebar-num">04</span>
                  <span class="sidebar-icon">${icon('list-checks')}</span>
                  <span class="sidebar-label">${esc(t('tabs.tasks'))}</span>
                </a>
                <a class="sidebar-link" href="#/tenant-requests" data-tab="/tenant-requests" data-role="side-tenant-requests">
                  <span class="sidebar-num">05</span>
                  <span class="sidebar-icon">${icon('paperclip')}</span>
                  <span class="sidebar-label">${esc(t('tabs.tenant_requests'))}</span>
                </a>
                <a class="sidebar-link" href="#/maintenance" data-tab="/maintenance" data-role="side-maintenance">
                  <span class="sidebar-num">06</span>
                  <span class="sidebar-icon">${icon('wrench')}</span>
                  <span class="sidebar-label">${esc(t('tabs.maintenance'))}</span>
                </a>
              </div>
              <div class="sidebar-group" data-role="side-marketing">
                <div class="sidebar-group-label eyebrow">${esc(t('app.nav.group.marketing'))}</div>
                <a class="sidebar-link" href="#/inquiries" data-tab="/inquiries" data-role="side-chat">
                  <span class="sidebar-num">06</span>
                  <span class="sidebar-icon">${icon('message-circle')}</span>
                  <span class="sidebar-label">${esc(t('tabs.chat'))}</span>
                </a>
              </div>
              <div class="sidebar-divider"></div>
              <div class="sidebar-group">
                <div class="sidebar-group-label eyebrow">${esc(t('app.nav.group.account'))}</div>
                <a class="sidebar-link" href="#/me" data-tab="/me">
                  <span class="sidebar-num">→</span>
                  <span class="sidebar-icon">${icon('user')}</span>
                  <span class="sidebar-label">${esc(t('tabs.me'))}</span>
                </a>
              </div>
            </nav>
          </aside>
          <main class="app-main" id="outlet"></main>
        </div>
        <nav class="tabbar" data-role="tabbar" hidden>
          <a class="tabbar-link" href="#/dashboard" data-tab="/dashboard" data-role="tab-dashboard">
            <span class="tabbar-icon">${icon('gauge')}</span>
            <span class="tabbar-label">${esc(t('tabs.dashboard'))}</span>
          </a>
          <a class="tabbar-link" href="#/properties" data-tab="/properties" data-role="tab-properties">
            <span class="tabbar-icon">${icon('building')}</span>
            <span class="tabbar-label">${esc(t('tabs.properties'))}</span>
          </a>
          <a class="tabbar-link" href="#/tenancies" data-tab="/tenancies">
            <span class="tabbar-icon">${icon('file-text')}</span>
            <span class="tabbar-label">${esc(t('tabs.tenancies'))}</span>
          </a>
          <a class="tabbar-link" href="#/tasks" data-tab="/tasks">
            <span class="tabbar-icon">${icon('list-checks')}</span>
            <span class="tabbar-label">${esc(t('tabs.tasks'))}</span>
          </a>
          <button type="button" class="tabbar-link tabbar-more" data-action="more"
                  aria-haspopup="menu" aria-expanded="false">
            <span class="tabbar-icon">${icon('more-horizontal')}</span>
            <span class="tabbar-label">${esc(t('tabs.more'))}</span>
          </button>
        </nav>
        <div class="tabsheet" data-role="tabsheet" hidden>
          <div class="tabsheet-backdrop" data-action="tabsheet-close"></div>
          <div class="tabsheet-panel" role="menu" aria-label="${esc(t('tabs.more'))}">
            <div class="tabsheet-header">
              <strong data-slot="sheet-name"></strong>
              <span class="faint" data-slot="sheet-email"></span>
            </div>
            <a class="tabsheet-link" href="#/tenants" data-tab="/tenants" data-role="tab-tenants" role="menuitem">
              <span class="tabsheet-icon">${icon('users')}</span>
              <span>${esc(t('tabs.tenants'))}</span>
            </a>
            <a class="tabsheet-link" href="#/tenant-requests" data-tab="/tenant-requests" data-role="tab-tenant-requests" role="menuitem">
              <span class="tabsheet-icon">${icon('paperclip')}</span>
              <span>${esc(t('tabs.tenant_requests'))}</span>
            </a>
            <a class="tabsheet-link" href="#/maintenance" data-tab="/maintenance" data-role="tab-maintenance" role="menuitem">
              <span class="tabsheet-icon">${icon('wrench')}</span>
              <span>${esc(t('tabs.maintenance'))}</span>
            </a>
            <a class="tabsheet-link" href="#/inquiries" data-tab="/inquiries" data-role="tab-chat" role="menuitem">
              <span class="tabsheet-icon">${icon('message-circle')}</span>
              <span>${esc(t('tabs.chat'))}</span>
            </a>
            <a class="tabsheet-link" href="#/me" data-tab="/me" role="menuitem">
              <span class="tabsheet-icon">${icon('user')}</span>
              <span>${esc(t('tabs.me'))}</span>
            </a>
            <button type="button" class="tabsheet-link tabsheet-signout" data-action="sheet-logout" role="menuitem">
              <span class="tabsheet-icon">${icon('log-out')}</span>
              <span>${esc(t('app.logout'))}</span>
            </button>
          </div>
        </div>
      </div>
    `;
    this.outlet = this.root.querySelector('#outlet');

    this.root.querySelector('[data-action="theme"]').addEventListener('click', (e) => {
      toggleTheme();
      e.currentTarget.innerHTML = this._themeIcon();
    });
    this.root.querySelector('[data-action="logout"]').addEventListener('click', () => {
      this._closeUserMenu();
      this._signOut();
    });
    this.root.querySelector('[data-action="user-menu-toggle"]').addEventListener('click', (e) => {
      e.stopPropagation();
      this._toggleUserMenu();
    });
    this.root.querySelector('[data-action="locale"]').addEventListener('change', (e) => {
      setLocale(e.target.value);
      // Simplest way to apply translations across all live views.
      window.location.reload();
    });
    this.root.querySelector('[data-action="more"]').addEventListener('click', (e) => {
      e.stopPropagation();
      this._toggleTabsheet();
    });
    this.root.querySelector('[data-action="tabsheet-close"]').addEventListener('click', () => this._closeTabsheet());
    this.root.querySelectorAll('.tabsheet-link[data-tab]').forEach((a) => {
      a.addEventListener('click', () => this._closeTabsheet());
    });
    this.root.querySelector('[data-action="sheet-logout"]').addEventListener('click', () => {
      this._closeTabsheet();
      this._signOut();
    });

    // Outside-click + Escape close the user menu when it's open.
    this._onDocClick = (e) => {
      const menu = this.root.querySelector('[data-role="user-menu"]');
      if (menu && !menu.contains(e.target)) this._closeUserMenu();
    };
    this._onDocKey = (e) => {
      if (e.key === 'Escape') { this._closeUserMenu(); this._closeTabsheet(); }
    };
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onDocKey);

    this._bell = new NotificationsBell();
    this._renderAuthState(auth.state);
    this._unsubAuth = auth.subscribe((state) => this._renderAuthState(state));
  }

  async _signOut() {
    try { await api.post('/auth/logout'); } catch (_) {}
    auth.clear();
    window.location.hash = '#/login';
  }

  _toggleUserMenu() {
    const panel = this.root.querySelector('[data-slot="user-menu-panel"]');
    const trigger = this.root.querySelector('[data-action="user-menu-toggle"]');
    if (!panel || !trigger) return;
    const open = panel.hidden;
    panel.hidden = !open;
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  _closeUserMenu() {
    const panel = this.root.querySelector('[data-slot="user-menu-panel"]');
    const trigger = this.root.querySelector('[data-action="user-menu-toggle"]');
    if (panel && !panel.hidden) panel.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  setActiveTab(path) {
    // Tabbar, More-sheet and desktop sidebar mark the current route the same way.
    this.root.querySelectorAll('[data-tab]').forEach((a) => {
      const tab = a.getAttribute('data-tab');
      a.classList.toggle('active', path.startsWith(tab));
    });
    // The More button lights up when the current destination lives in its sheet.
    const more = this.root.querySelector('[data-action="more"]');
    if (more) more.classList.toggle('active', !!this.root.querySelector('.tabsheet-link.active'));
    // Navigating anywhere dismisses an open sheet.
    this._closeTabsheet();
  }

  _toggleTabsheet() {
    const sheet = this.root.querySelector('[data-role="tabsheet"]');
    const trigger = this.root.querySelector('[data-action="more"]');
    if (!sheet || !trigger) return;
    const open = sheet.hidden;
    sheet.hidden = !open;
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  _closeTabsheet() {
    const sheet = this.root.querySelector('[data-role="tabsheet"]');
    const trigger = this.root.querySelector('[data-action="more"]');
    if (sheet && !sheet.hidden) sheet.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  _renderAuthState(state) {
    const isAuthed = !!state.accessToken;
    const tabbar = this.root.querySelector('[data-role="tabbar"]');
    const sidebar = this.root.querySelector('[data-role="sidebar"]');
    const propertiesTab = this.root.querySelector('[data-role="tab-properties"]');
    const tenantsTab = this.root.querySelector('[data-role="tab-tenants"]');
    const sideTenants = this.root.querySelector('[data-role="side-tenants"]');
    const chatTab = this.root.querySelector('[data-role="tab-chat"]');
    const requestsTab = this.root.querySelector('[data-role="tab-tenant-requests"]');
    const sideProperties = this.root.querySelector('[data-role="side-properties"]');
    const sideChat = this.root.querySelector('[data-role="side-chat"]');
    const sideRequests = this.root.querySelector('[data-role="side-tenant-requests"]');
    const sideSetupGroup = this.root.querySelector('[data-role="side-setup"]');
    const sideMarketingGroup = this.root.querySelector('[data-role="side-marketing"]');
    const sideMaintenance = this.root.querySelector('[data-role="side-maintenance"]');
    const tabMaintenance = this.root.querySelector('[data-role="tab-maintenance"]');
    const userMenu = this.root.querySelector('[data-role="user-menu"]');
    const title = this.root.querySelector('.appbar h1');
    const bellSlot = this.root.querySelector('[data-slot="bell"]');
    if (tabbar) tabbar.hidden = !isAuthed;
    if (sidebar) sidebar.hidden = !isAuthed;
    if (propertiesTab) propertiesTab.hidden = !isAuthed || !auth.canManageProperties;
    if (tenantsTab) tenantsTab.hidden = !isAuthed || !auth.canManageProperties;
    if (sideTenants) sideTenants.hidden = !isAuthed || !auth.canManageProperties;
    if (chatTab) chatTab.hidden = !isAuthed || !auth.canManageProperties || !auth.hasFeature('marketplace');
    if (sideProperties) sideProperties.hidden = !isAuthed || !auth.canManageProperties;
    if (sideChat) sideChat.hidden = !isAuthed || !auth.canManageProperties || !auth.hasFeature('marketplace');
    // Setup and Marketing groups each currently host a single manager-only link;
    // hide the whole group (label included) when that link is hidden so we don't
    // leave a dangling eyebrow heading in the tenant sidebar.
    if (sideSetupGroup) sideSetupGroup.hidden = !isAuthed || !auth.canManageProperties;
    if (sideMarketingGroup) sideMarketingGroup.hidden = !isAuthed || !auth.canManageProperties || !auth.hasFeature('marketplace');
    const requestsHref = '#' + (auth.canManageProperties ? '/tenant-requests/outgoing' : '/tenant-requests/incoming');
    if (requestsTab) {
      requestsTab.hidden = !isAuthed || !auth.hasFeature('tenant_requests');
      // Managers naturally land on the outbox they created; tenants on their inbox.
      requestsTab.setAttribute('href', requestsHref);
    }
    if (sideRequests) {
      sideRequests.hidden = !isAuthed || !auth.hasFeature('tenant_requests');
      sideRequests.setAttribute('href', requestsHref);
    }
    // Hibajelentések is for both sides — tenants report, landlords manage.
    if (sideMaintenance) sideMaintenance.hidden = !isAuthed || !auth.hasFeature('maintenance');
    if (tabMaintenance) tabMaintenance.hidden = !isAuthed || !auth.hasFeature('maintenance');
    if (userMenu) {
      // The wrapper is hidden until auth lands; CSS @media gates desktop-only display.
      userMenu.hidden = !isAuthed;
      userMenu.classList.toggle('is-authed', isAuthed);
      if (!isAuthed) this._closeUserMenu();
    }
    if (!isAuthed) this._closeTabsheet();
    if (bellSlot) {
      bellSlot.hidden = !isAuthed;
      if (isAuthed && this._bell && !this._bell._mounted) this._bell.render(bellSlot);
    }
    if (title) title.textContent = t('app.name');
    if (isAuthed && state.user) this._renderUserChip(state.user);
  }

  _renderUserChip(user) {
    const avatar = this.root.querySelector('[data-slot="avatar"]');
    const nameEl = this.root.querySelector('[data-slot="menu-name"]');
    const emailEl = this.root.querySelector('[data-slot="menu-email"]');
    const trigger = this.root.querySelector('[data-action="user-menu-toggle"]');
    const sheetName = this.root.querySelector('[data-slot="sheet-name"]');
    const sheetEmail = this.root.querySelector('[data-slot="sheet-email"]');
    const fullName = (user.full_name || '').trim();
    const email = user.email || '';
    if (avatar) avatar.textContent = this._initials(fullName, email);
    if (nameEl) nameEl.textContent = fullName || email || '';
    if (emailEl) emailEl.textContent = email;
    if (sheetName) sheetName.textContent = fullName || email || '';
    if (sheetEmail) sheetEmail.textContent = email;
    if (trigger) trigger.setAttribute('aria-label', `${fullName || email} — ${t('app.logout')}`);
  }

  _initials(fullName, email) {
    const src = (fullName || email || '').trim();
    if (!src) return '?';
    const parts = src.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return src.slice(0, 2).toUpperCase();
  }

  _themeIcon() {
    return icon(currentTheme() === 'dark' ? 'sun' : 'moon', { label: 'Toggle theme' });
  }

  _brandLogo() {
    return `
      <svg class="appbar-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
        <rect width="64" height="64" rx="14" fill="#0f766e"/>
        <path d="M16 38 L32 22 L48 38 L48 50 L36 50 L36 40 L28 40 L28 50 L16 50 Z" fill="#ffffff"/>
      </svg>
    `;
  }
}

// Build-time release metadata injected by Vite (see vite.config.js).
// __APP_VERSION__ is the patch-bumped frontend/package.json version,
// __APP_COMMIT__ is the short HEAD sha at build time, __APP_BUILD_TIME__
// the ISO timestamp. Each deploy bumps the patch in deploy.sh.
function versionLabel() {
  // eslint-disable-next-line no-undef
  return `v${__APP_VERSION__}`;
}
function buildLabel() {
  // eslint-disable-next-line no-undef
  return `v${__APP_VERSION__} · ${__APP_COMMIT__} · built ${__APP_BUILD_TIME__}`;
}
